import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit01 } from '@untitledui/icons';
import type { ComponentType } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import {
  attachSecret,
  mintEntitySecret,
  webhookAuthMethodToSecretFormat,
} from '@/lib/crm/secrets/service';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue, unwrapPropertyValue } from '@/types/crm';

import { conflictKey } from '../../components/associations/conflict-key';
import { ConflictResolutionPanel } from '../../components/associations/ConflictResolutionPanel';
import { buildEditInBuilderNavState } from '../../components/badges/buildBuilderModeNavState';
import { CrmFieldValueInput } from '../../components/fields/CrmFieldValueInput';
import { JsonPropertyEditFields } from '../../components/properties/JsonPropertyFields';
import type { WebhookSecretCreatedData } from '../../components/webhooks/WebhookSecretCreatedModal';
import { WebhookSecretCreatedModal } from '../../components/webhooks/WebhookSecretCreatedModal';
import { UnsavedChangesProvider } from '../../contexts/UnsavedChangesContext';
import type {
  AssociationType,
  CrmObjectTypeSchema,
} from '../../core/SchemaClient';
import { useAssociationConflicts } from '../../hooks/useAssociationConflicts';
import { useAssociationFieldSubsets } from '../../hooks/useAssociationFieldSubsets';
import { useAssociationMatchers } from '../../hooks/useAssociationMatchers';
import { useAssociations } from '../../hooks/useAssociations';
import { useFormState } from '../../hooks/useFormState';
import { useObjectTypes } from '../../hooks/useObjectTypes';
import {
  type PropertyTypeSchema,
  usePropertySchema,
} from '../../hooks/usePropertySchema';
import { useStageTypeNames } from '../../hooks/useStageTypeNames';
import type { DetailField } from '../../record/registry/entityTypeRegistry';
import {
  detailFieldKey,
  getEntityTypeConfig,
} from '../../record/registry/entityTypeRegistry';
import { HIDDEN_ASSOCIATION_TARGETS } from '../../record/registry/fieldConstants';
import { upsertSummaryObjects } from '../../store/crmObjectStore';
import {
  hasAssociationValue,
  isAssociationValueFrozen,
} from '../../utils/associationEditability';
import { isAssociationVisibleOnOverview } from '../../utils/associationMatching';
import { getPropertyTypeIconComponent } from '../../utils/icons/PropertyTypeIcon';
import { isPropertyEditable } from '../../utils/propertyEditability';
import type { SaveBarState } from '../types';
import {
  buildDetailFieldList,
  decomposedTargetTypesFor,
  expandKindBasedAssociationFields,
  isDecomposableRule,
  partitionAdvancedFields,
} from '../utils/buildDetailFieldList';
import { CollapsibleSection } from './collapsible-section';

/** Record type whose secrets mint through the generic secrets flow. */
// Seeds the builder composer when editing a rule's formulas. Names no record —
// the rule is open in the panel beside the chat and travels as context — but it
// DOES name the field, because a rule has two formulas ("When" and "Validate")
// and both carry this affordance. A single fixed prompt described only the
// first, so clicking edit on Validate asked the agent to change the other one.
const editRulePrompt = (fieldLabel: string) =>
  `I want to change this rule's ${fieldLabel} formula`;

/** Stable empty fallback so a loading object-types query doesn't churn memos. */
const EMPTY_TYPES: CrmObjectTypeSchema[] = [];

const API_CREDENTIAL_ENTITY_TYPE = 'ahoy_service_account';
/**
 * Secrets row on an API credential.
 *
 * These keys are the association type as seen FROM the record being viewed, so
 * each must name the direction whose source is that record — the same direction
 * whose label is "Secrets" in association_types.yaml. For a credential that is
 * `ahoy_principal_to_secret` (source: principal, target: ahoy_secret), NOT its
 * reverse `ahoy_secret_to_principal` (source: ahoy_secret, label "Authenticates"),
 * which is the row you see on a secret's own record.
 *
 * Getting this backwards silently removes the mint button rather than erroring:
 * the key misses CUSTOM_SECRET_ASSOCIATIONS, so no onCustomCreate is passed, and
 * the generic create path is then gated off because ahoy_secret's entity kind is
 * `empty` (AssociatedCrmObjectInput's isCreatableTargetKind allows party/case/asset
 * only). The row renders with a picker and no way to create.
 */
const API_CREDENTIAL_SECRET_ASSOCIATION = 'ahoy_principal_to_secret';
/** Secrets rows on webhooks — minting has its own endpoint. */
const WEBHOOK_SECRET_ASSOCIATION = 'ahoy_webhook_to_secret';
const OUTBOUND_WEBHOOK_SECRET_ASSOCIATION = 'ahoy_outbound_webhook_to_secret';

/**
 * Association rows whose "add" affordance mints a bearer secret rather than
 * picking an existing record, so they opt out of the standard create flow.
 */
const CUSTOM_SECRET_ASSOCIATIONS = new Set([
  API_CREDENTIAL_SECRET_ASSOCIATION,
  WEBHOOK_SECRET_ASSOCIATION,
  OUTBOUND_WEBHOOK_SECRET_ASSOCIATION,
]);

/**
 * Get the current association value from the object's associations array.
 * Returns entity_id(s) for the given association type.
 */
function getAssociationValue(
  object: CrmObject,
  associationTypeName: string,
  cardinality: string
): string | string[] | null {
  const associations = (object.associations || []).filter(
    assoc => assoc.association_type_name === associationTypeName
  );

  if (associations.length === 0) {
    return null;
  }

  // For single-select associations, return just the entity_id string
  if (cardinality === 'many_to_one' || cardinality === 'one_to_one') {
    return associations[0].target_entity_id || null;
  }

  // For multi-select associations, return array of entity_ids
  return associations.map(a => a.target_entity_id).filter(Boolean);
}

// Note: the per-field change-summary tooltip (formatChangeValue +
// buildChangeSummary) was removed when the inline sticky save bar moved
// out of this component and into the surface-level floating SaveBar.
// The "N unsaved changes" label on the SaveBar provides a coarse hint;
// re-adding the field-level diff tooltip on the floating bar is tracked
// as a follow-up — see git history for the previous implementation if
// it needs to come back.

// Right-aligned edit affordance overlaid inside a validation rule's read-only
// formula box (When / Validate). Formulas aren't inline-editable — clicking
// opens a chat seeded with the rule so the assistant can rewrite them.
function EditWithAssistantButton({
  label,
  onEdit,
}: {
  label: string;
  onEdit: () => void;
}) {
  const title = `Edit ${label} with the assistant`;
  return (
    <button
      type='button'
      onClick={onEdit}
      title={title}
      aria-label={title}
      className='absolute right-2 top-1/2 -translate-y-1/2 shrink-0 p-1 rounded text-fg-quaternary transition-colors hover:text-fg-secondary hover:bg-bg-tertiary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring cursor-pointer'
    >
      <Edit01 className='w-4 h-4' aria-hidden='true' />
    </button>
  );
}

interface PropertyFieldRowProps {
  field: DetailField;
  propSchema: PropertyTypeSchema;
  value: string | number | object | null | undefined;
  updateProperty: (key: string, value: unknown) => void;
  /**
   * Optional custom editor component from entityTypeRegistry.customPropertyEditors.
   * When provided, replaces the generic CrmFieldValueInput for this property.
   */
  customEditor?: ComponentType<{
    value: unknown;
    onChange: (next: unknown) => void;
    disabled: boolean;
  }>;
  orgId: number;
  /**
   * The entity's raw `properties` map (e.g. `object.properties`), forwarded
   * so a read-only `formula` field can resolve its target entity type from a
   * sibling property.
   */
  entityProperties?: Record<string, unknown>;
  /**
   * When set, the field renders an "edit with the assistant" icon overlaid on
   * the right of its value box (used for a validation rule's read-only
   * When/Validate formulas). Clicking invokes this handler with the field's
   * own label, so the composer can name the formula the user actually clicked
   * rather than guessing at one of the two.
   */
  onEditWithAssistant?: (fieldLabel: string) => void;
}

function PropertyFieldRow({
  field,
  propSchema,
  value,
  updateProperty,
  customEditor: CustomEditor,
  orgId,
  entityProperties,
  onEditWithAssistant,
}: PropertyFieldRowProps) {
  const handleChange = useCallback(
    (val: unknown) => updateProperty(field.key, val),
    [updateProperty, field.key]
  );
  // The row is the only thing that knows which field it is, so it names itself
  // on the way out. Bound here rather than inline in the JSX below to keep the
  // reference stable.
  const handleEditWithAssistant = useCallback(
    () => onEditWithAssistant?.(propSchema.label),
    [onEditWithAssistant, propSchema.label]
  );
  const IconComponent = getPropertyTypeIconComponent(propSchema);

  // Respect access control in the edit form: a property the user can't update
  // (immutable after creation, or internal/system-managed) renders disabled so
  // the UI never invites an edit the backend would reject. Shared with the
  // list table's inline cell editing — see propertyEditability.ts.
  const isEditable = isPropertyEditable(propSchema);

  return (
    <div className='flex items-start gap-2'>
      <IconComponent
        className='w-3.5 h-3.5 text-fg-quaternary flex-shrink-0 mt-0.5'
        aria-hidden='true'
      />
      <div className='flex-1 min-w-0'>
        <div className='text-xs md:text-sm text-fg-quaternary mb-1'>
          {propSchema.label}
        </div>
        {CustomEditor ? (
          <CustomEditor
            value={value}
            onChange={handleChange}
            disabled={!isEditable}
          />
        ) : (
          <div className='relative'>
            <CrmFieldValueInput
              kind='property'
              schema={propSchema}
              value={value}
              onChange={handleChange}
              orgId={orgId}
              entityProperties={entityProperties}
              disabled={!isEditable}
            />
            {onEditWithAssistant && (
              <EditWithAssistantButton
                label={propSchema.label}
                onEdit={handleEditWithAssistant}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface AssociationFieldRowProps {
  assocType: AssociationType;
  /**
   * The FORM key this row reads and writes — the association type name, or
   * `${type}::${targetType}` when the row is one per-type subset of a
   * kind-based rule and several rows share one association type.
   */
  fieldKey: string;
  /** Names a decomposed row after its target type ("Deals"), not the rule. */
  labelOverride?: string;
  /** Restricts the picker to one target type, for a decomposed row. */
  targetEntityTypeName?: string;
  value: string | string[] | null | undefined;
  updateAssociation: (key: string, value: string | string[] | null) => void;
  orgId: number;
  sourceEntityId: string;
  stageTypeNames: Set<string>;
  /** Custom create handler for non-standard create flows (e.g. secrets). */
  onCustomCreate?: () => void;
  /** Suppress the picker, leaving only the create affordance (e.g. secrets). */
  createOnly?: boolean;
  /** When true, conflicting entities remain selectable; conflicts surface via ConflictResolutionPanel. */
  allowOverwrite?: boolean;
}

function AssociationFieldRow({
  assocType,
  fieldKey,
  labelOverride,
  targetEntityTypeName,
  value,
  updateAssociation,
  orgId,
  sourceEntityId,
  stageTypeNames,
  onCustomCreate,
  createOnly,
  allowOverwrite,
}: AssociationFieldRowProps) {
  // Writes to the FIELD key, not the association type: two subsets of one rule
  // are two independent form entries, which is what keeps a change to "Deals"
  // from being diffed against the record's tasks.
  const handleChange = useCallback(
    (val: string | string[] | null) => updateAssociation(fieldKey, val),
    [updateAssociation, fieldKey]
  );

  // Write-once associations (e.g. "Created by") freeze once set: the backend
  // rejects the removal the cardinality cascade would otherwise emit, so an
  // editable input here could only produce a failed write and an error toast.
  // Still rendered — read-only — because the value is worth showing.
  const isFrozen = isAssociationValueFrozen(
    assocType,
    hasAssociationValue(value)
  );

  return (
    <div className='flex items-start gap-2'>
      <div className='w-3.5 h-3.5 flex-shrink-0' aria-hidden='true' />
      <div className='flex-1 min-w-0'>
        <div className='text-xs md:text-sm text-fg-quaternary mb-1'>
          {labelOverride ?? assocType.label}
        </div>
        <CrmFieldValueInput
          kind='association'
          associationType={assocType}
          targetEntityTypeName={targetEntityTypeName}
          value={value}
          onChange={handleChange}
          orgId={orgId}
          disabled={assocType.value_write_access === 'internal' || isFrozen}
          sourceEntityId={sourceEntityId}
          stageTypeNames={stageTypeNames}
          onCustomCreate={onCustomCreate}
          createOnly={createOnly}
          allowOverwrite={allowOverwrite}
          // Suppress the stage input's mount-time default auto-select.
          // We're editing an existing record — if the user's record has no
          // stage set (or a stage that no longer exists), entering Edit
          // shouldn't silently fire onChange with a default and mark the
          // form as "1 unsaved change". The user must explicitly pick a
          // stage. No-op for non-stage associations.
          disableAutoSelect
        />
      </div>
    </div>
  );
}

interface JsonPropertyFieldEditRowProps {
  fieldKey: string;
  schema: Record<string, unknown> | null;
  value: Record<string, unknown> | null;
  orgId: number;
  sourceEntityTypeName: string;
  sourceEntityId: string;
  updateProperty: (key: string, value: unknown) => void;
}

function JsonPropertyFieldEditRow({
  fieldKey,
  schema,
  value,
  orgId,
  sourceEntityTypeName,
  sourceEntityId,
  updateProperty,
}: JsonPropertyFieldEditRowProps) {
  const handleChange = useCallback(
    (updatedConfig: Record<string, unknown>) =>
      updateProperty(fieldKey, updatedConfig),
    [updateProperty, fieldKey]
  );

  return (
    <JsonPropertyEditFields
      schema={schema}
      value={value}
      orgId={orgId}
      sourceEntityTypeName={sourceEntityTypeName}
      sourceEntityId={sourceEntityId}
      onChange={handleChange}
    />
  );
}

interface OverviewEditSectionProps {
  object: CrmObject;
  onSaveStateChange: (state: SaveBarState | null) => void;
  onExitEdit: () => void;
}

export function OverviewEditSection({
  object,
  onSaveStateChange,
  onExitEdit,
}: OverviewEditSectionProps) {
  const orgId = object.org_id;
  const entityTypeName = object.entity_type_name;
  const config = getEntityTypeConfig(entityTypeName);
  const { user } = useAuthInfo();

  // A validation rule's When/Validate formulas are read-only here; their
  // overlaid edit icon opens builder mode with the rule in the panel, so the
  // assistant can rewrite them with the rule still in view. Previously this
  // landed on the conversations home screen with only a seeded composer — the
  // rule itself was left behind.
  const navigate = useNavigate();
  const isBuilderModeEnabled = useFeatureFlag('builder_mode');
  const handleEditFormulaWithAssistant = useCallback(
    (fieldLabel: string) => {
      const prompt = editRulePrompt(fieldLabel);
      if (isBuilderModeEnabled) {
        const { to, state } = buildEditInBuilderNavState(
          orgId,
          entityTypeName,
          object.entity_id,
          prompt
        );
        navigate(to, { state });
        return;
      }
      // Flag off — the pre-builder destination: the conversations home screen,
      // seeded and with the rule attached as context. The prompt names no
      // record either way; `entityIds` is what grounds the agent.
      navigate(`/${orgId}/conversations`, {
        state: {
          initialPrompt: prompt,
          fromConversationsHome: true,
          entityIds: [object.entity_id],
        },
      });
    },
    [navigate, orgId, object.entity_id, entityTypeName, isBuilderModeEnabled]
  );

  const {
    schema,
    loading: schemaLoading,
    error: schemaError,
  } = usePropertySchema(orgId, entityTypeName);

  // Use kind-aware matchers so kind-based outgoing associations (e.g.
  // contact_to_note, which has source_entity_kind_name=person) are included
  // in `initialAssociations` and `editableAssociationTypes`. The plain
  // `at.source_entity_type_name === entityTypeName` check missed them.
  const {
    associationTypes,
    isLoading: associationTypesLoading,
    matchesSource,
    entityKindMap,
  } = useAssociationMatchers(orgId);

  const { stageTypeNames } = useStageTypeNames(orgId);

  const objectTypesQuery = useObjectTypes(orgId);
  const allObjectTypes = objectTypesQuery.data?.allObjectTypes ?? EMPTY_TYPES;

  const assocTypeByName = useMemo(() => {
    const map = new Map<string, AssociationType>();
    for (const at of associationTypes) {
      map.set(at.association_type_name, at);
    }
    return map;
  }, [associationTypes]);

  /** entity_type_name -> its plural label, for naming a decomposed field. */
  const typeLabelPluralMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of allObjectTypes) {
      map.set(t.entity_type_name, t.label_plural || t.label);
    }
    return map;
  }, [allObjectTypes]);

  /**
   * A field key maps back to the association type a write must emit —
   * `${type}::${targetType}` carries the subset, the event carries the rule.
   */
  const resolveAssociationTypeName = useCallback(
    (formKey: string) => formKey.split('::')[0],
    []
  );

  // Resolved association targets, so a kind-based rule can be split into one
  // field per target type. Reads the store first (the record view that opened
  // this form already hydrated them) and batch-fetches only what is missing.
  const { associations: resolvedAssociations } = useAssociations(
    orgId,
    object.associations ?? [],
    { sourceEntityTypeName: object.entity_type_name }
  );
  // No read-mode extras — every resolvable type gets a field here regardless.
  const { assocSubsets } = useAssociationFieldSubsets(resolvedAssociations);

  /**
   * The per-type fields a kind-based rule contributes to THIS form.
   *
   * Unlike the read surface, every resolvable type gets a field even when the
   * record has none of it — that empty "Deals" row is how the first deal gets
   * linked. Mirrors what `expandKindBasedAssociationFields` produces for
   * `mode: 'edit'` (which passes no `presentTargetTypesByAssoc`).
   */
  const decomposedEditFields = useMemo(() => {
    const fields: Array<
      Extract<DetailField, { type: 'association' }> & {
        targetEntityTypeName: string;
      }
    > = [];
    for (const at of associationTypes) {
      if (
        !matchesSource(at, object.entity_type_name) ||
        !isDecomposableRule(at)
      ) {
        continue;
      }
      // Same decision the read surface makes — a rule resolving to one type
      // stays undecomposed and contributes no per-type field here.
      const targetTypes = decomposedTargetTypesFor(at, allObjectTypes);
      if (targetTypes === null) {
        continue;
      }
      for (const targetEntityTypeName of targetTypes) {
        fields.push({
          type: 'association',
          key: at.association_type_name,
          targetEntityTypeName,
        });
      }
    }
    return fields;
  }, [
    associationTypes,
    matchesSource,
    object.entity_type_name,
    allObjectTypes,
  ]);

  // Compute initial property values (extract raw values from PropertyValue wrappers)
  const initialProperties = useMemo(() => {
    const props = object.properties || {};
    const rawValues: Record<string, unknown> = {};
    for (const [key, propValue] of Object.entries(props)) {
      // obj.properties[key] is always a single-level {value} wrapper — unwrap
      // via unwrapPropertyValue rather than reimplementing the check inline.
      // Multi-value (array) properties must pass through unchanged, matching
      // prior behavior — unwrapPropertyValue only unwraps a bare {value}
      // wrapper and leaves arrays untouched.
      rawValues[key] = unwrapPropertyValue(propValue);
    }
    return rawValues;
  }, [object.properties]);

  // Compute initial association values for relevant association types
  const initialAssociations = useMemo(() => {
    if (
      associationTypesLoading ||
      associationTypes.length === 0 ||
      !object.entity_type_name
    ) {
      return {};
    }

    const associations: Record<string, string | string[] | null> = {};
    const relevantAssocTypes = associationTypes.filter(at =>
      matchesSource(at, object.entity_type_name)
    );

    relevantAssocTypes.forEach(assocType => {
      associations[assocType.association_type_name] = getAssociationValue(
        object,
        assocType.association_type_name,
        assocType.cardinality
      );
    });

    // A kind-based rule is edited as one field per resolved target type, so its
    // single value is split into per-type entries keyed `${type}::${target}`.
    // The rule's own key is dropped: leaving it would make the form diff the
    // whole set against a subset and report every other type as removed.
    const decomposedRuleKeys = new Set<string>();
    for (const [assocTypeName, subsets] of assocSubsets) {
      const idsByType = new Map<string, string[]>();
      for (const [targetType, objects] of subsets) {
        idsByType.set(
          targetType,
          objects.map(o => o.entity_id)
        );
      }
      for (const field of decomposedEditFields) {
        if (field.key !== assocTypeName || !field.targetEntityTypeName) {
          continue;
        }
        associations[detailFieldKey(field)] =
          idsByType.get(field.targetEntityTypeName) ?? [];
        decomposedRuleKeys.add(assocTypeName);
      }
    }
    return Object.fromEntries(
      Object.entries(associations).filter(
        ([key]) => !decomposedRuleKeys.has(key)
      )
    );
  }, [
    associationTypes,
    associationTypesLoading,
    matchesSource,
    object,
    assocSubsets,
    decomposedEditFields,
  ]);

  // Per-item set of conflict compound keys (entity_id|association_type_name) the
  // user has acknowledged overwriting. Declared before useFormState so the
  // memoised array can be passed as option.
  const [ackedEntityIds, setAckedEntityIds] = useState<Set<string>>(new Set());

  // Derive per-conflict overwrite targets from the compound keys. Authorization
  // is scoped per (entity_id, association_type_name) pair so acknowledging one
  // conflict cannot cause unintended removals on a different association type
  // targeting the same entity.
  const overwriteTargets = useMemo(
    () =>
      Array.from(ackedEntityIds).map(k => {
        const [entity_id, association_type_name] = k.split('|');
        return { entity_id, association_type_name };
      }),
    [ackedEntityIds]
  );

  const requiredPropertyKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const [key, propSchema] of Object.entries(schema)) {
      if (propSchema?.is_required) {
        keys.add(key);
      }
    }
    return keys;
  }, [schema]);

  const {
    currentProperties,
    currentAssociations,
    updateProperty,
    updateAssociation,
    changeCount,
    pendingChanges,
    saveChanges,
    resetChanges,
    isSaving,
    saveError,
  } = useFormState({
    orgId,
    userId: user?.id || 0,
    object,
    initialProperties,
    initialAssociations,
    requiredPropertyKeys,
    onSaveSuccess: onExitEdit,
    overwriteTargets,
    resolveAssociationTypeName,
  });

  // Build the associations list for conflict pre-validation: only added ones
  // require cardinality checking; removes are always safe.
  const conflictAssociations = useMemo(
    () =>
      pendingChanges.addedAssociations.map(a => ({
        association_type_name: a.type,
        target_entity_id: a.targetId,
      })),
    [pendingChanges.addedAssociations]
  );

  // Prune ack keys whose (entity_id, association_type_name) pair is no longer
  // a pending addition. Without this, removing an association via the picker
  // (rather than via the conflict panel's Remove button) leaves a stale ack
  // behind. The stale ack would leak into overwrite_targets on the next submit.
  useEffect(() => {
    setAckedEntityIds(prev => {
      if (prev.size === 0) {
        return prev;
      }
      const pendingKeys = new Set(
        pendingChanges.addedAssociations.map(a =>
          conflictKey(a.targetId, a.type)
        )
      );
      let mutated = false;
      const next = new Set<string>();
      for (const key of prev) {
        if (pendingKeys.has(key)) {
          next.add(key);
        } else {
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [pendingChanges.addedAssociations]);

  const { conflicts, error: conflictsError } = useAssociationConflicts({
    orgId,
    sourceEntityType: entityTypeName,
    sourceEntityId: object.entity_id,
    associations: conflictAssociations,
  });

  const handleAcknowledge = useCallback(
    (entityId: string, associationTypeName: string, checked: boolean) => {
      setAckedEntityIds(prev => {
        const next = new Set(prev);
        const key = conflictKey(entityId, associationTypeName);
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    []
  );

  const handleRemoveConflictTarget = useCallback(
    (entityId: string, associationTypeName: string) => {
      const pendingAdd = pendingChanges.addedAssociations.find(
        a => a.targetId === entityId && a.type === associationTypeName
      );
      if (!pendingAdd) {
        return;
      }
      // Read and write the FIELD the target was added through, not the
      // association type: a decomposed rule has no entry under its own name,
      // and two of its subsets can be in the form at once.
      const currentValue = currentAssociations[pendingAdd.formKey];
      const newValue = Array.isArray(currentValue)
        ? currentValue.filter(id => id !== entityId)
        : null;
      updateAssociation(pendingAdd.formKey, newValue);
      setAckedEntityIds(prev => {
        const next = new Set(prev);
        next.delete(conflictKey(entityId, associationTypeName));
        return next;
      });
    },
    [pendingChanges.addedAssociations, currentAssociations, updateAssociation]
  );

  // Gate: blocked when there are known unresolved conflicts or a validation error.
  // Does NOT block during the initial check (conflicts.length === 0 while checking)
  // to avoid freezing Save every time the user types in an association field.
  const allConflictsAcked =
    conflicts.length === 0 ||
    conflicts.every(c =>
      ackedEntityIds.has(
        conflictKey(c.entity_id, c.association_type_name ?? '')
      )
    );
  const blockedByConflicts =
    conflictAssociations.length > 0 &&
    (conflictsError !== null || (conflicts.length > 0 && !allConflictsAcked));

  // Stable callbacks for unsaved changes guard
  const handleGuardDiscard = useCallback(() => {
    setAckedEntityIds(new Set());
    resetChanges();
    onExitEdit();
  }, [resetChanges, onExitEdit]);

  // Non-throwing save handler — used by Save button, errors shown inline
  const handleSave = useCallback(async () => {
    await saveChanges();
  }, [saveChanges]);

  // Throwing save handler — used by UnsavedChangesModal guard and unsaved-changes context
  const handleSaveGuarded = useCallback(async () => {
    const result = await saveChanges();
    if (!result.success) {
      throw new Error(result.error || 'Save failed');
    }
    // onExitEdit is called by saveChanges via onSaveSuccess — no need to call again
  }, [saveChanges]);

  // Unsaved changes guard — browser/router navigation is handled by the parent
  // SingleSlideout, so we disable it here to avoid blocking without a modal.
  const { guard } = useUnsavedChangesGuard({
    hasUnsavedChanges: changeCount > 0,
    onDiscard: handleGuardDiscard,
    onSave: handleSaveGuarded,
    blockRouterNavigation: false,
    blockBrowserClose: false,
  });

  // Stable ref to avoid stale closures in useEffect
  const onSaveStateChangeRef = useRef(onSaveStateChange);
  onSaveStateChangeRef.current = onSaveStateChange;

  // Emit save state to parent on every relevant change
  useEffect(() => {
    onSaveStateChangeRef.current({
      changeCount,
      onSave: handleSave,
      onSaveGuarded: handleSaveGuarded,
      onCancel: handleGuardDiscard,
      isSaving,
      error: saveError,
      guard,
      blockedByConflicts,
      blockedReason: blockedByConflicts
        ? 'Resolve association conflicts below to save'
        : undefined,
    });
  }, [
    changeCount,
    handleSave,
    handleSaveGuarded,
    handleGuardDiscard,
    isSaving,
    saveError,
    guard,
    blockedByConflicts,
  ]);

  // Clear save state on unmount
  useEffect(() => {
    return () => {
      onSaveStateChangeRef.current(null);
    };
  }, []);

  // Unsaved changes context value for UnsavedChangesProvider
  const unsavedChangesContextValue = useMemo(
    () => ({
      hasUnsavedChanges: changeCount > 0,
      guard,
      onSave: handleSaveGuarded,
    }),
    [changeCount, guard, handleSaveGuarded]
  );

  // ---- Secret creation for webhook entities ----
  const queryClient = useQueryClient();
  const [secretModalData, setSecretModalData] =
    useState<WebhookSecretCreatedData | null>(null);

  const createSecretMutation = useMutation({
    mutationFn: async (): Promise<WebhookSecretCreatedData> => {
      // Two mint flows share this button: an API credential's secret is
      // written secret -> principal via `attachSecret`; a webhook's secret is
      // written principal -> secret via `mintEntitySecret` (the webhook's own
      // creator-scoped write grant is the authorization gate -- see
      // `mintEntitySecret`'s doc comment). Both surface the token in the same
      // one-time modal.
      if (entityTypeName === API_CREDENTIAL_ENTITY_TYPE) {
        const { secretEntityId, token, masked_hint } = await attachSecret(
          orgId,
          user.id,
          object.entity_id
        );
        return {
          bearer_token: token,
          secret_hint: masked_hint,
          secret_entity_id: secretEntityId,
        };
      }

      const isOutbound = entityTypeName === 'ahoy_outbound_webhook';
      const authMethod = getPropertyValue<string>(
        object.properties?.ahoy_webhook_auth_method
      );
      // D-9: a MISSING ahoy_webhook_enabled means enabled (born enabled), so only
      // an explicit 'false' counts as disabled -- mirrors resolve_endpoint's gate.
      // Without this, the first mint on a fresh endpoint would write a needless
      // explicit enable.
      const wasDisabled =
        isOutbound &&
        getPropertyValue<string>(object.properties?.ahoy_webhook_enabled) ===
          'false';

      const { secretEntityId, token, masked_hint } = await mintEntitySecret(
        orgId,
        user.id,
        object.entity_id,
        entityTypeName,
        {
          secretEntityType: 'ahoy_automation_webhook_secret',
          associationType: isOutbound
            ? OUTBOUND_WEBHOOK_SECRET_ASSOCIATION
            : WEBHOOK_SECRET_ASSOCIATION,
          format: webhookAuthMethodToSecretFormat(authMethod),
          // Mirrors the webhook's own re-enable-on-mint convenience: rotating
          // an outbound endpoint's secret while it's breaker-tripped disabled
          // re-enables it in the same batch, matching D-9 (endpoints are born
          // enabled).
          extraEvents: wasDisabled
            ? builder =>
                builder.propertyUpdated(
                  object.entity_id,
                  entityTypeName,
                  'ahoy_webhook_enabled',
                  'true'
                )
            : undefined,
        }
      );
      return {
        bearer_token: token,
        secret_hint: masked_hint,
        secret_entity_id: secretEntityId,
      };
    },
    onSuccess: data => {
      setSecretModalData(data);
      // Invalidate the object detail query so the new association appears
      void queryClient.invalidateQueries({
        queryKey: ['crm', orgId, 'object', object.entity_id],
      });
      // Upsert a minimal secret entity into the CRM store for display.
      // `secret_hint` is the masked hint (`••••` + suffix) -- the same value
      // already stamped onto `ahoy_secret_hint` server-side, so this optimistic
      // entry matches what the eventual websocket update will confirm.
      upsertSummaryObjects([
        {
          id: data.secret_entity_id,
          org_id: orgId,
          entity_id: data.secret_entity_id,
          entity_type_name: (entityTypeName === API_CREDENTIAL_ENTITY_TYPE
            ? 'ahoy_secret'
            : 'ahoy_automation_webhook_secret') as CrmObject['entity_type_name'],
          properties: {
            ahoy_display_name: { value: data.secret_hint },
            ahoy_secret_hint: { value: data.secret_hint },
          },
          last_event_seq: 1,
          deleted_at: null,
        },
      ]);
    },
    onError: err => {
      logger.error(
        'Failed to create secret',
        err instanceof Error ? err : new Error(String(err)),
        { entityId: object.entity_id }
      );
      toast.error("We couldn't create the secret. Try again.");
    },
  });

  const handleCreateSecret = useCallback(() => {
    createSecretMutation.mutate();
  }, [createSecretMutation]);

  const handleCloseSecretModal = useCallback(() => {
    setSecretModalData(null);
  }, []);

  // Filter association types to editable, relevant ones.
  // Uses the shared kind predicate (isAssociationVisibleOnOverview) with denylist
  // semantics: visible unless the target's kind is in HIDDEN_TARGET_KINDS
  // (activity, event, reporting).
  //   - HIDDEN_ASSOCIATION_TARGETS (note, file, meeting, booking, booking_type)
  //   - Kind denylist via isAssociationVisibleOnOverview
  //   - Read/write access controls
  const editableAssociationTypes = useMemo((): AssociationType[] => {
    if (associationTypesLoading) {
      return [];
    }
    return associationTypes.filter(at => {
      if (!matchesSource(at, entityTypeName)) {
        return false;
      }
      // Hidden targets (notes, files, meetings, etc.)
      // WORKAROUND until #12411: see fieldConstants.ts HIDDEN_ASSOCIATION_TARGETS
      if (HIDDEN_ASSOCIATION_TARGETS.has(at.target_entity_type_name)) {
        return false;
      }
      if (at.value_read_access !== 'all') {
        return false;
      }
      // v1 PropertiesTab: internal write-access associations are still shown
      // if they already have a value (displayed read-only). Replicate that.
      //
      // A decomposed rule has no entry under its own name — its value is split
      // across `${type}::${targetType}` keys — so ask whether ANY of its
      // subsets holds a value rather than reading the bare association type,
      // which would always be empty and silently drop the row.
      if (at.value_write_access === 'internal') {
        return Object.entries(initialAssociations).some(
          ([formKey, value]) =>
            (formKey === at.association_type_name ||
              formKey.startsWith(`${at.association_type_name}::`)) &&
            hasAssociationValue(value)
        );
      }
      if (!isAssociationVisibleOnOverview(at, entityKindMap)) {
        return false;
      }
      return true;
    });
  }, [
    associationTypes,
    associationTypesLoading,
    entityTypeName,
    matchesSource,
    entityKindMap,
    initialAssociations,
  ]);

  // Build ordered edit fields using same detailFieldOrder as read-only view
  const editFields = useMemo((): DetailField[] => {
    const editableAssocTypeNames = new Set(
      editableAssociationTypes.map(at => at.association_type_name)
    );
    return expandKindBasedAssociationFields(
      buildDetailFieldList({
        mode: 'edit',
        config,
        schema,
        editableAssocTypeNames,
        editableAssociationTypes,
      }),
      {
        assocTypeByName,
        objectTypes: allObjectTypes,
        // No `presentTargetTypesByAssoc`: the edit form keeps a field for every
        // resolvable type, empty or not, so the first target can be linked.
      }
    );
  }, [
    config,
    schema,
    editableAssociationTypes,
    assocTypeByName,
    allObjectTypes,
  ]);

  // Split out fields configured as `advancedFields` into a collapsed-by-default
  // Advanced group, rendered below the main list but still part of the same
  // form state (currentProperties/updateProperty), so they save normally.
  const { main: mainFields, advanced: advancedFields } = useMemo(
    () =>
      partitionAdvancedFields(editFields, new Set(config.advancedFields ?? [])),
    [editFields, config.advancedFields]
  );

  // Build association type map for quick lookup
  const assocTypeMap = useMemo(() => {
    const map = new Map<string, AssociationType>();
    for (const at of editableAssociationTypes) {
      map.set(at.association_type_name, at);
    }
    return map;
  }, [editableAssociationTypes]);

  // Shared per-field renderer used by both the main list and the Advanced
  // group so their rows render identically.
  const renderEditField = useCallback(
    (field: DetailField) => {
      if (field.type === 'property') {
        const propSchema: PropertyTypeSchema | undefined = schema[field.key];
        if (!propSchema) {
          return null;
        }

        // JSON properties: decompose into individual fields when a
        // companion schema is available. Skip entirely otherwise —
        // PropertyInput has no raw JSON editor.
        if (propSchema.field_type_name === 'json') {
          if (!propSchema.schema_property) {
            return null;
          }
          const schemaValue = getPropertyValue<Record<string, unknown>>(
            object.properties?.[propSchema.schema_property]
          );
          if (
            schemaValue &&
            typeof schemaValue === 'object' &&
            'properties' in schemaValue
          ) {
            return (
              <JsonPropertyFieldEditRow
                key={field.key}
                fieldKey={field.key}
                schema={schemaValue}
                value={
                  currentProperties[field.key] as Record<string, unknown> | null
                }
                orgId={orgId}
                sourceEntityTypeName={entityTypeName}
                sourceEntityId={object.entity_id}
                updateProperty={updateProperty}
              />
            );
          }
          // Companion schema missing/malformed — nothing to render.
          return null;
        }

        // json_schema properties are companions to json fields,
        // not directly editable.
        if (propSchema.field_type_name === 'json_schema') {
          return null;
        }

        return (
          <PropertyFieldRow
            key={field.key}
            field={field}
            propSchema={propSchema}
            value={
              currentProperties[field.key] as
                | string
                | number
                | object
                | null
                | undefined
            }
            updateProperty={updateProperty}
            customEditor={config.customPropertyEditors?.[field.key]}
            orgId={orgId}
            entityProperties={currentProperties}
            onEditWithAssistant={
              entityTypeName === 'ahoy_validation_rule' &&
              propSchema.field_type_name === 'formula'
                ? handleEditFormulaWithAssistant
                : undefined
            }
          />
        );
      }

      if (field.type === 'association') {
        const assocType = assocTypeMap.get(field.key);
        if (!assocType) {
          return null;
        }
        const fieldKey = detailFieldKey(field);
        return (
          <AssociationFieldRow
            key={fieldKey}
            fieldKey={fieldKey}
            assocType={assocType}
            // A decomposed field is named for the records it holds ("Deals"),
            // not the rule — whose label covers every type of its target kind.
            labelOverride={
              field.targetEntityTypeName
                ? typeLabelPluralMap.get(field.targetEntityTypeName)
                : undefined
            }
            targetEntityTypeName={field.targetEntityTypeName}
            value={currentAssociations[fieldKey]}
            updateAssociation={updateAssociation}
            orgId={orgId}
            sourceEntityId={object.entity_id}
            stageTypeNames={stageTypeNames}
            onCustomCreate={
              CUSTOM_SECRET_ASSOCIATIONS.has(field.key)
                ? handleCreateSecret
                : undefined
            }
            createOnly={CUSTOM_SECRET_ASSOCIATIONS.has(field.key)}
            allowOverwrite
          />
        );
      }

      return null;
    },
    [
      schema,
      object.properties,
      object.entity_id,
      currentProperties,
      orgId,
      entityTypeName,
      updateProperty,
      config.customPropertyEditors,
      assocTypeMap,
      currentAssociations,
      updateAssociation,
      stageTypeNames,
      typeLabelPluralMap,
      handleCreateSecret,
      handleEditFormulaWithAssistant,
    ]
  );

  if (schemaLoading || associationTypesLoading) {
    return (
      <div className='space-y-3 pt-2' aria-busy='true'>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className='flex items-center gap-2'>
            <div className='w-5 h-5 bg-[var(--color-bg-tertiary)] rounded animate-pulse flex-shrink-0' />
            <div className='w-24 h-4 bg-[var(--color-bg-tertiary)] rounded animate-pulse flex-shrink-0' />
            <div className='flex-1 h-4 bg-[var(--color-bg-tertiary)] rounded animate-pulse' />
          </div>
        ))}
      </div>
    );
  }

  if (schemaError) {
    return (
      <div className='p-3 text-xs md:text-sm text-[var(--color-text-error-primary)]'>
        We couldn't load the schema. Close and reopen the panel.
      </div>
    );
  }

  return (
    <UnsavedChangesProvider value={unsavedChangesContextValue}>
      {/* Font size deliberately NOT set here. It belongs to each control's
          `size` prop (the shared scale in forms/select/select-shared.ts), which
          is responsive on its own. The previous `[&_input]:text-xs
          [&_input]:md:text-sm` style overrides compiled to descendant rules
          (specificity 0,1,1) that beat each control's own `text-*` (0,1,0) —
          but only for controls whose font class sits on an `input`/`textarea`/
          `select`/`button`. Select puts its `text-md` on an inner span and
          PhoneInput pins its height with `!important`, so those two escaped and
          rendered 40px while plain text inputs collapsed to 36px. Reaching in
          by selector cannot produce consistent heights; pass `size` instead. */}
      <div className='space-y-3 pt-1 [&_button]:font-normal'>
        {mainFields.map(renderEditField)}

        {advancedFields.length > 0 && (
          <CollapsibleSection title='Advanced' defaultExpanded={false}>
            <div className='space-y-3'>
              {advancedFields.map(renderEditField)}
            </div>
          </CollapsibleSection>
        )}

        {(conflicts.length > 0 || conflictsError !== null) && (
          <div className='mt-3 pt-3 border-t border-[var(--color-border-secondary)]'>
            <ConflictResolutionPanel
              conflicts={conflicts}
              error={conflictsError}
              acknowledged={ackedEntityIds}
              onAcknowledge={handleAcknowledge}
              onRemoveTarget={handleRemoveConflictTarget}
            />
          </div>
        )}

        {/*
          The Save/Cancel controls used to live here as a `sticky -bottom-5`
          row, but the AccordionPanel ancestor wraps its children in an
          `overflow-hidden` div which clips the sticky-containing-block to
          this section's natural height — so the bar never actually stuck
          and users had to scroll to the bottom to find it.

          The floating SaveBar is now rendered at the surface level
          (SingleSlideout / CRMObjectFullPageView), absolutely positioned
          against a `relative` ancestor outside the accordion. Cancel lives
          in OverviewSection's header so users can always exit edit mode
          even when there are no pending changes (the SaveBar only renders
          when changes exist). The state continues to flow up via the
          `onSaveStateChange` effect above; no changes to the contract.
        */}
      </div>
      <WebhookSecretCreatedModal
        isOpen={secretModalData !== null}
        onClose={handleCloseSecretModal}
        data={secretModalData}
      />
    </UnsavedChangesProvider>
  );
}
